import { FC, useEffect, useState } from "react";
import { TablePagination } from "@mui/material";
import { useSearchParams } from "react-router-dom";

export interface CustomPaginationProps {
  isLoading: boolean;
  limit: number;
  page: number;
  count: number;
  setToPage: (page: number) => void;
  setLimit: (limit: number) => void;
}

const CustomPagination: FC<CustomPaginationProps> = (props) => {
  const [_, setSearchParams] = useSearchParams();
  const { isLoading, limit, page, count, setToPage, setLimit } = props;
  const [data, setData] = useState<{
    limit: number;
    page: number;
    count: number;
  }>({
    limit,
    page,
    count,
  });

  useEffect(() => {
    // If we finished loading in the data, or we click on a previous page we update the numbers
    // this prevents weird NaN and jumping behaviour and is essentially caching the data
    // for the duration of the fetch
    if (!isLoading || page < data.page) {
      setData({
        limit,
        page,
        count,
      });
    }
    if (!isLoading && page * limit - limit + 1 > count) {
      setToPage(1);
      setSearchParams((params) => {
        params.delete("page");
        return params;
      });
    }
  }, [isLoading, page, limit, count]);

  const {
    limit: internalLimit,
    page: internalPage,
    count: internalCount,
  } = data;

  return (
    <TablePagination
      component="div"
      rowsPerPageOptions={[10, 20, 50, 100, 200]}
      defaultValue={internalLimit}
      count={internalCount}
      page={internalPage - 1}
      rowsPerPage={internalLimit}
      onPageChange={(_, page) => {
        const value = page + 1;
        setToPage(value);
        setSearchParams((params) => {
          value !== 1
            ? params.set("page", String(value))
            : params.delete("page");
          return params;
        });
      }}
      onRowsPerPageChange={(e) => {
        const value = parseInt(e.target.value);
        setLimit(value);
        // Reset to the first page so we never land on an out-of-range page.
        // Otherwise, raising the limit while on the last page can leave the
        // page beyond the available records and surface the empty state.
        setToPage(1);
        setSearchParams((params) => {
          value !== 10
            ? params.set("limit", e.target.value)
            : params.delete("limit");
          params.delete("page");
          return params;
        });
      }}
    />
  );
};

export default CustomPagination;
